import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
    Output,
    EventEmitter,
    Injector,
} from '@angular/core';
import { DocumentTemplateService } from '@fi-app/document-template/document-template.service';
import { TemplateModel } from '@fi-app/template/template.model';
import { TemplateLayoutService } from '@fi-app/template/template-layout.service';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { DocumentModel } from '../../document.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';

interface ITemplatePlaceHolderControlContentValue {
    placeHolderControlId: number;
    contentLanguageId: number;
    value: string;
}

@Component({
    selector: 'fi-document-edit-content',
    templateUrl: './content.component.html',
    styleUrls: ['./content.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class DocumentEditContentComponent implements OnChanges {
    @Input() document: DocumentModel;
    @Input() device;
    @Input() selectedTemplateId: number;
    @Input() languageId: number;

    @Output() documentTemplateLayoutChange = new EventEmitter();
    @Output() documentTemplateChange = new EventEmitter();
    @Output() contentChange = new EventEmitter();

    // store original template content values for 'reset to template' functionality
    private templatePlaceHolderContentValues: ITemplatePlaceHolderControlContentValue[] = [];
    documentTemplateLayout: TemplateLayoutModel;
    constructor(
        private documentTemplateService: DocumentTemplateService,
        private templateLayoutService: TemplateLayoutService,
        private fiStateService: FiStateService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private injector: Injector,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedTemplateId) this.templateIdChanged(changes.selectedTemplateId.firstChange);
    }

    templateLoading: boolean = false;

    onResetToTemplateClick() {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('document.reset_to_template_header'),
                messageText: this.appTextService.get('document.reset_to_template_message'),
                yesButtonText: this.appTextService.get('document.reset_to_template'),
            })
            .onYesButtonClick(() => {
                this.document.DocumentControlContents.forEach((controlContent) => {
                    if (controlContent.ContentLanguageId === this.languageId) {
                        const origControlContent = this.templatePlaceHolderContentValues.find(
                            (item) =>
                                item.contentLanguageId === controlContent.ContentLanguageId &&
                                item.placeHolderControlId === controlContent.PlaceHolderControlId,
                        );
                        if (origControlContent) {
                            controlContent.Value = JSON.parse(origControlContent.value);
                        }
                    }
                });
                this.templateLayoutService.ApplyDocumentControlContentsToTemplate(
                    this.document.DocumentControlContents,
                    this.document.Template,
                );
                this.document.Template.PlaceHolderCollection.forEach((placeHolder) => {
                    placeHolder.ControlCollection.forEach((control) => {
                        control.Contents.forEach((controlContent) => {
                            if (controlContent.ContentLanguageId === this.languageId) {
                                const origControlContent = this.templatePlaceHolderContentValues.find(
                                    (item) =>
                                        item.contentLanguageId === controlContent.ContentLanguageId &&
                                        item.placeHolderControlId === controlContent.PlaceHolderControlId,
                                );
                                if (origControlContent) {
                                    controlContent.Value = JSON.parse(origControlContent.value);
                                }
                            }
                        });
                    });
                });
                this.templateLayoutService.TemplatePlaceHolderCollectionToLayout(
                    this.document.Template.PlaceHolderCollection,
                    this.documentTemplateLayout,
                );
            });
    }
    saveTemplatePlaceHolderContentValues(template: TemplateModel) {
        const result: ITemplatePlaceHolderControlContentValue[] = [];
        template.PlaceHolderCollection.forEach((placeHolder) => {
            placeHolder.ControlCollection.forEach((control) => {
                control.Contents.forEach((controlContent) => {
                    result.push({
                        contentLanguageId: controlContent.ContentLanguageId,
                        placeHolderControlId: controlContent.PlaceHolderControlId,
                        value: JSON.stringify(controlContent.Value),
                    });
                });
            });
        });
        this.templatePlaceHolderContentValues = result;
    }

    private templateIdChanged(firstChange: boolean) {
        if (this.selectedTemplateId) {
            this.templateLoading = true;
            this.documentTemplateService.GetTemplate(this.selectedTemplateId, { skipAccessCheck: true }).then(
                (response) => {
                    this.document.Template = response;
                    this.saveTemplatePlaceHolderContentValues(this.document.Template);
                    /*if (!firstChange || (!this.document.Id && this.fiStateService.current.name !== FiStateNames.Document.Copy)) {
                    this.document.MediaBankFileReferences.length = 0;
                    this.document.Template.MediaBankFileReferences
                        .forEach(templateMediaBankFileReference => this.document.MediaBankFileReferences.push(templateMediaBankFileReference));
                }*/

                    this.initDocumentTemplateLayout(this.document.Template);

                    this.templateLoading = false;
                },
                () => {
                    this.templateLoading = false;
                },
            );
        } else {
            this.documentTemplateLayout = null;
        }
    }

    private initDocumentTemplateLayout(selectedTemplate: TemplateModel) {
        if (selectedTemplate) {
            const template = new TemplateModel(Object.assign({}, selectedTemplate), this.injector);
            this.documentTemplateLayout = this.templateLayoutService.GetLayout(template, this.document);
            this.templateLayoutService.ApplyDocumentControlContentsToTemplate(
                this.document.DocumentControlContents,
                template,
            );
            this.templateLayoutService.TemplatePlaceHolderCollectionToLayout(
                template.PlaceHolderCollection,
                this.documentTemplateLayout,
            );
            // TemplateLayoutLanguageService.InitDefaultLanguageFromLayout(this.documentTemplateLayout);
        } else {
            this.documentTemplateLayout = null;
        }
        this.documentTemplateLayoutChange.emit(this.documentTemplateLayout);
        this.documentTemplateChange.emit(selectedTemplate);
    }

    mediaBankFileChanged($event) {
        // todo not used?
        if (!this.document.MediaBankFileIds) this.document.MediaBankFileIds = {};

        if ($event.oldId) this.document.MediaBankFileIds[$event.oldId] = false;

        if ($event.newId) this.document.MediaBankFileIds[$event.newId] = true;
    }

    documentLayoutContentChange($event) {
        this.contentChange.emit($event);
    }
}
